import {
  AlertCircle,
  Bike,
  Calendar,
  ChevronDown,
  Filter,
  Loader2,
  Minus,
  Plus,
  RotateCcw,
} from "lucide-react";
import { format } from "date-fns";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
// import biciMontana from '../assets/images/bici-montana.jpg';
import { useBooking } from "../contexts/BookingContext";
import { useAccessories } from "../hooks/useAccessories";
import { useAvailability } from "../hooks/useAvailability";
import { useCategories } from "../hooks/useCategories";
import { useClosedDays } from "../hooks/useClosedDays";
import { useTranslation } from "../hooks/useTranslation";
import "../styles/bike-catalog-light.css";
import "../styles/flip-card.css";
import { formatPricePerDay } from "../utils/priceFormatter";
import DoubleDateRangePicker from "./DoubleDateRangePicker";
import PriceBreakdown from "./PriceBreakdown";
import StepContainer from "./StepContainer";

// Todas las tallas posibles ordenadas (puede extenderse hasta 7 tallas)
const ALL_POSSIBLE_SIZES = ["XS", "S", "M", "ML", "L", "XL", "XXL"];

/**
 * Flip por hover solo en escritorios anchos con ratón.
 * Por debajo de ~iPad Pro 12.9" en horizontal (1366px) se muestra el botón aunque
 * DevTools siga reportando pointer:fine.
 */
const shouldUseHoverCardFlip = () =>
  typeof window !== "undefined" &&
  window.innerWidth >= 1367 &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/** Imagen principal de la card con indicador de carga hasta que el navegador termine de descargarla */
const BikeCatalogModelImage = ({ t, src, alt }) => {
  const [status, setStatus] = useState("loading");
  const imgRef = useRef(null);

  // useLayoutEffect evita carrera: imagen en caché dispara onLoad antes que useEffect y quedaba en "loading".
  useLayoutEffect(() => {
    if (!src) {
      setStatus("loading");
      return;
    }
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      setStatus("loaded");
    } else {
      setStatus("loading");
    }
  }, [src]);

  if (!src) {
    return (
      <div className="w-full h-full min-h-[10rem] flex flex-col items-center justify-center gap-2 text-6c757d text-xs px-2 text-center">
        <Bike size={28} className="opacity-40" />
        <span>{t("bike.image.none")}</span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full min-h-[10rem] flex items-center justify-center">
      {status !== "loaded" && status !== "error" && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-gray-50 to-gray-100"
          role="status"
          aria-live="polite"
          aria-label={t("bike.image.loading")}
        >
          <Loader2
            size={28}
            className="text-primary animate-spin"
            aria-hidden
          />
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-gradient-to-br from-gray-50 to-gray-100 text-6c757d text-xs px-2 text-center">
          <AlertCircle size={22} className="text-yellow-600" />
          <span>{t("bike.image.error")}</span>
        </div>
      )}
      <img
        ref={imgRef}
        key={src}
        src={src}
        alt={alt}
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        className={`max-w-full max-h-full object-contain object-center transition-opacity duration-300 ${
          status === "loaded" ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

const BikeCatalog = ({
  selectedDates,
  onNext,
  onBack,
  deliveryType = "pickup",
  initialCategory = "",
  initialModel = "",
}) => {
  const [availabilityData, setAvailabilityData] = useState(null);
  const [filters, setFilters] = useState({
    category: initialCategory || "",
    size: "",
    modelId: initialModel || "",
  });
  const [flippedCards, setFlippedCards] = useState(new Set());
  const {
    bookingData,
    updateBookingData,
    updatePricingWithSeasons,
    clearAvailabilityCache,
  } = useBooking();
  const selectedVehicles = bookingData?.selectedVehicles || [];

  const {
    checkAvailability,
    availabilityLoading,
    availabilityError,
    cancelRequest,
  } = useAvailability();

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategories();
  const { accessories } = useAccessories();
  const { t } = useTranslation();
  const { closedDays } = useClosedDays();

  const hasDateRange = Boolean(selectedDates?.start && selectedDates?.end);

  /**
   * Obtiene la descripción del precio considerando el contexto de precios calculados
   * @param {Object} model - Modelo de vehículo
   * @returns {string} Descripción del precio
   */
  const getPriceDescriptionWithContext = (model) => {
    // Priorizar precio estacional si está disponible
    if (
      typeof model.pricePerDay === "number" &&
      !Number.isNaN(model.pricePerDay)
    ) {
      return formatPricePerDay(model.pricePerDay, t("date.day"));
    }

    // Mostrar "Desde" seguido del precio del additionalDay como fallback
    const additionalDayPrice = model.additionalDay || 0;
    return `${t("bike.price.from")} ${formatPricePerDay(
      additionalDayPrice,
      t("date.day")
    )}`;
  };

  useEffect(() => {
    // Solo ejecutar si hay fechas válidas
    if (!selectedDates?.start || !selectedDates?.end) {
      setAvailabilityData(null);
      return;
    }

    let isMounted = true;

    const loadAvailability = async () => {
      try {
        const data = await checkAvailability(
          selectedDates.start,
          selectedDates.end
        );

        // Solo actualizar si el componente sigue montado y recibimos datos válidos
        if (isMounted) {
          if (data !== null) {
            setAvailabilityData(data);
          } else {
            // Si data es null, podría ser una cancelación, pero aún así resetear
            setAvailabilityData(null);
          }
        }
      } catch {
        // Solo actualizar si el componente sigue montado
        if (isMounted) {
          setAvailabilityData(null);
        }
      }
    };

    loadAvailability();

    // Cleanup: cancelar petición pendiente si cambian las fechas o se desmonta el componente
    return () => {
      isMounted = false;
      cancelRequest();
    };
  }, [
    selectedDates?.start,
    selectedDates?.end,
    checkAvailability,
    cancelRequest,
  ]);

  // Actualizar precios de vehículos seleccionados cuando cambien las fechas y haya nueva disponibilidad
  useEffect(() => {
    if (
      !availabilityData ||
      !selectedVehicles ||
      selectedVehicles.length === 0
    ) {
      return;
    }

    const removed = [];

    const updatedVehicles = selectedVehicles
      .map((vehicle) => {
        const model = availabilityData.find((m) => m.id === vehicle.modelId);
        if (!model) {
          removed.push({
            id: vehicle.id,
            model: vehicle.model,
            size: vehicle.size,
            serialNumber: vehicle.serialNumber,
            reason: "model-not-found",
          });
          return null;
        }

        const sizeData = model?.availability?.bySize?.[vehicle.size];
        const availableVehicleIds = Array.isArray(sizeData?.vehicles)
          ? sizeData.vehicles.map((v) => v?.id).filter(Boolean)
          : [];
        const isVehicleStillAvailable = availableVehicleIds.includes(vehicle.id);

        if (!isVehicleStillAvailable) {
          removed.push({
            id: vehicle.id,
            model: vehicle.model,
            size: vehicle.size,
            serialNumber: vehicle.serialNumber,
            reason: "vehicle-not-available",
          });
          return null;
        }

        return {
          ...vehicle,
          basePrice:
            model.pricingType === "seasonal" || model.pricingType === "mixed"
              ? model.seasonBreakdown?.[0]?.basePrice || model.basePrice
              : model.basePrice,
          threeToFive:
            model.pricingType === "seasonal" || model.pricingType === "mixed"
              ? model.seasonBreakdown?.[0]?.threeToFive || model.threeToFive
              : model.threeToFive,
          additionalDay:
            model.pricingType === "seasonal" || model.pricingType === "mixed"
              ? model.seasonBreakdown?.[0]?.additionalDay || model.additionalDay
              : model.additionalDay,
          pricingType: model.pricingType,
          season: model.season,
          seasonBreakdown: model.seasonBreakdown,
          totalPrice: model.totalPrice,
          pricePerDay: model.pricePerDay,
          pricingStart: selectedDates?.start || null,
          pricingEnd: selectedDates?.end || null,
        };
      })
      .filter(Boolean);

    // Solo actualizar si hay cambios en los precios
    const hasChanges =
      removed.length > 0 ||
      updatedVehicles.some((updatedVehicle, index) => {
        const originalVehicle = selectedVehicles[index];
        return (
          updatedVehicle?.totalPrice !== originalVehicle?.totalPrice ||
          updatedVehicle?.pricePerDay !== originalVehicle?.pricePerDay
        );
      });

    if (hasChanges) {
      const nextAccessories = { ...(bookingData?.selectedAccessories || {}) };
      const nextInseam = { ...(bookingData?.inseamCmByVehicleId || {}) };
      removed.forEach((r) => {
        delete nextAccessories[r.id];
        delete nextInseam[r.id];
      });

      updateBookingData({
        selectedVehicles: updatedVehicles,
        selectedAccessories: nextAccessories,
        inseamCmByVehicleId: nextInseam,
        availabilityIssues:
          removed.length > 0
            ? {
                start: selectedDates?.start || null,
                end: selectedDates?.end || null,
                removed,
              }
            : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityData, selectedDates?.start, selectedDates?.end]);

  // Función para manejar el cambio de fechas desde el DateRangePicker
  const handleDateRangeChange = async (startDateStr, endDateStr) => {
    // Rango a medio elegir (solo inicio): esperar a que el usuario elija el fin
    if (Boolean(startDateStr) !== Boolean(endDateStr)) return;

    // Si se limpian fechas desde el datepicker, resetear estado asociado
    if (!startDateStr || !endDateStr) {
      clearAvailabilityCache();
      setAvailabilityData(null);
      updateBookingData({
        dates: {
          start: null,
          end: null,
          startTime: null,
          endTime: null,
        },
        selectedVehicles: [],
        selectedAccessories: {},
        availabilityIssues: null,
      });
      return;
    }

    // Validar que la fecha de inicio no sea anterior a hoy
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedStart = new Date(startDateStr);
    selectedStart.setHours(0, 0, 0, 0);

    if (selectedStart < today) {
      return;
    }

    // Validar que la fecha de fin no sea anterior a la de inicio
    const selectedEnd = new Date(endDateStr);
    selectedEnd.setHours(0, 0, 0, 0);

    if (selectedEnd < selectedStart) {
      return;
    }

    // No se puede recoger ni devolver en un día cerrado (el calendario ya los deshabilita)
    if (closedDays.has(startDateStr) || closedDays.has(endDateStr)) {
      return;
    }

    // Limpiar el caché de disponibilidad
    clearAvailabilityCache();

    // Crear fechas con horarios fijos (9:00 AM para inicio, 21:00 PM para fin)
    const startDateTime = new Date(startDateStr + "T09:00:00");
    const endDateTime = new Date(endDateStr + "T21:00:00");

    const newDates = {
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      startTime: "09:00:00",
      endTime: "21:00:00",
    };

    // Si hay vehículos seleccionados, recalcular los precios con las nuevas fechas
    if (selectedVehicles && selectedVehicles.length > 0) {
      await updatePricingWithSeasons({
        dates: newDates,
      });
    } else {
      // Si no hay vehículos seleccionados, solo actualizar las fechas
      updateBookingData({
        dates: newDates,
      });
    }
  };

  // Actualizar el contexto con los accesorios cargados
  useEffect(() => {
    if (
      accessories &&
      accessories.length > 0 &&
      (!bookingData.accessories || bookingData.accessories.length === 0)
    ) {
      updateBookingData({ accessories });
    }
  }, [accessories, bookingData.accessories, updateBookingData]);

  const handleVehicleSelection = (modelId, size, vehicleId, action) => {
    // Validar parámetros de entrada
    if (!modelId || !size || !vehicleId || !action) {
      return;
    }

    const model = availabilityData?.find((m) => m?.id === modelId);
    const sizeData = model?.availability?.bySize?.[size];
    const vehicle = sizeData?.vehicles?.find((v) => v?.id === vehicleId);

    if (!model || !sizeData || !vehicle) {
      return;
    }

    const newSelection = [...(selectedVehicles || [])];

    if (action === "add") {
      // Verificar si el vehículo ya está seleccionado
      if (!newSelection.some((v) => v.id === vehicleId)) {
        newSelection.push({
          id: vehicleId,
          modelId,
          model: model.name,
          inseamOptionalEnabled: Boolean(model.inseamOptionalEnabled),
          size,
          // Usar precios estacionales si están disponibles, sino usar precios base
          basePrice:
            model.pricingType === "seasonal" || model.pricingType === "mixed"
              ? model.seasonBreakdown?.[0]?.basePrice || model.basePrice
              : model.basePrice,
          threeToFive:
            model.pricingType === "seasonal" || model.pricingType === "mixed"
              ? model.seasonBreakdown?.[0]?.threeToFive || model.threeToFive
              : model.threeToFive,
          additionalDay:
            model.pricingType === "seasonal" || model.pricingType === "mixed"
              ? model.seasonBreakdown?.[0]?.additionalDay || model.additionalDay
              : model.additionalDay,
          // Agregar información de temporada
          pricingType: model.pricingType,
          season: model.season,
          seasonBreakdown: model.seasonBreakdown,
          // Usar el precio total calculado por el backend
          totalPrice: model.totalPrice,
          pricePerDay: model.pricePerDay,
          // Guardar para qué fechas se calculó este precio (para invalidar al cambiar fechas)
          pricingStart: selectedDates?.start || null,
          pricingEnd: selectedDates?.end || null,
          serialNumber: vehicle.serialNumber,
        });
      }
    } else if (action === "remove") {
      // Eliminar el vehículo de la selección
      const index = newSelection.findIndex((v) => v.id === vehicleId);
      if (index !== -1) {
        newSelection.splice(index, 1);
      }
    }

    // Actualizar los vehículos seleccionados y recalcular precios inmediatamente
    // para que el total se actualice en tiempo real cuando se selecciona más de una bicicleta
    updatePricingWithSeasons({ selectedVehicles: newSelection });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const toggleCardFlip = (modelId) => {
    setFlippedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  const filteredModels = availabilityData?.filter((model) => {
    if (filters.category && model.category?.id !== parseInt(filters.category))
      return false;
    if (
      filters.size &&
      !model.availability.availableSizes.includes(filters.size)
    )
      return false;
    if (filters.modelId && model.id !== parseInt(filters.modelId)) return false;
    return true;
  });

  const getSelectedCount = (modelId, size) => {
    if (!selectedVehicles || !Array.isArray(selectedVehicles)) {
      return 0;
    }
    return selectedVehicles.filter(
      (v) => v && v.modelId === modelId && v.size === size
    ).length;
  };

  // Función para ordenar las tallas de menor a mayor
  const sortSizes = (sizes) => {
    if (!sizes || sizes.length === 0) return [];

    // Ordenar según el orden de ALL_POSSIBLE_SIZES
    const orderedSizes = ALL_POSSIBLE_SIZES.filter((size) =>
      sizes.includes(size)
    );
    // Agregar cualquier otra talla que no esté en ALL_POSSIBLE_SIZES y ordenarla alfabéticamente
    const otherSizes = sizes
      .filter((size) => !ALL_POSSIBLE_SIZES.includes(size))
      .sort();

    return [...orderedSizes, ...otherSizes];
  };

  // Obtener todas las tallas únicas disponibles en todos los modelos
  const getAllAvailableSizes = () => {
    if (!availabilityData || !Array.isArray(availabilityData)) {
      return ALL_POSSIBLE_SIZES;
    }
    const sizesSet = new Set();
    availabilityData.forEach((model) => {
      if (model.availability?.availableSizes) {
        model.availability.availableSizes.forEach((size) => sizesSet.add(size));
      }
    });
    // Ordenar según el orden de ALL_POSSIBLE_SIZES y luego agregar cualquier otra talla
    return sortSizes(Array.from(sizesSet));
  };

  // Solo mostrar loading inicial si hay fechas y estamos cargando disponibilidad (o categorías).
  // Sin fechas, availabilityData es null a propósito: no mostrar spinner infinito.
  if (
    categoriesLoading ||
    (hasDateRange && availabilityLoading && !availabilityData)
  ) {
    return (
      <StepContainer>
        <div className="flex flex-col items-center justify-center py-16 min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-EBC122 mb-4"></div>
          <p className="text-2c3e50 text-lg font-medium">{t("bike.loading")}</p>
        </div>
      </StepContainer>
    );
  }

  if (availabilityError || categoriesError) {
    return (
      <StepContainer>
        <div className="alert alert-error">
          <div className="flex gap-2">
            <AlertCircle size={20} />
            <span>{availabilityError || categoriesError}</span>
          </div>
        </div>
      </StepContainer>
    );
  }

  return (
    <div className="bike-catalog-light">
      <div className="w-full mx-auto p-0 md:p-4 pt-0">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-2c3e50 font-orbitron mb-2">
            {t("bike.title")}
          </h2>
        </div>

        {/* Filtros: mobile sin w-full; desktop fila fija para que el datepicker no desplace los demás */}
        <div className="mb-8 filters-section">
          <div className="flex flex-col md:flex-row md:flex-nowrap gap-4 md:gap-4 items-stretch md:items-start">
            {/* Selector de fechas: self-start para que la fila no crezca al abrir el modal */}
            <div className="md:flex-1 md:min-w-0 md:self-start overflow-visible space-y-2">
              <label className="block text-sm font-medium text-2c3e50 flex items-center gap-2">
                <Calendar size={16} className="text-primary flex-shrink-0" />
                {t("bike.dates.label")}
              </label>
              <DoubleDateRangePicker
                startDate={selectedDates?.start ? format(new Date(selectedDates.start), "yyyy-MM-dd") : ""}
                endDate={selectedDates?.end ? format(new Date(selectedDates.end), "yyyy-MM-dd") : ""}
                onChange={handleDateRangeChange}
                minDate={new Date()}
                closedDates={closedDays}
              />
            </div>
            <div className="md:flex-1 md:min-w-0 space-y-2">
              <label className="block text-sm font-medium text-2c3e50">
                {t("bike.filter.category")}
              </label>
              <div className="relative">
                <select
                  className="bike-filter-select w-full appearance-none border border-primary text-2c3e50 rounded-lg pl-4 pr-10 py-3 text-sm outline-none transition-all duration-200 focus:border-primary focus:ring-1 focus:ring-primary"
                  value={filters.category}
                  onChange={(e) => handleFilterChange("category", e.target.value)}
                >
                  <option value="">{t("bike.filter.all.categories")}</option>
                  {categories?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-primary"
                />
              </div>
            </div>
            <div className="md:flex-1 md:min-w-0 space-y-2">
              <label className="block text-sm font-medium text-2c3e50">
                {t("bike.filter.size")}
              </label>
              <div className="relative">
                <select
                  className="bike-filter-select w-full appearance-none border border-primary text-2c3e50 rounded-lg pl-4 pr-10 py-3 text-sm outline-none transition-all duration-200 focus:border-primary focus:ring-1 focus:ring-primary"
                  value={filters.size}
                  onChange={(e) => handleFilterChange("size", e.target.value)}
                >
                  <option value="">{t("bike.filter.all.sizes")}</option>
                  {getAllAvailableSizes().map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Resumen de precios - Mostrar arriba en móviles */}
        <div className="lg:hidden mb-6">
          <PriceBreakdown
            selectedVehicles={selectedVehicles}
            selectedDates={selectedDates}
            deliveryType={deliveryType}
            selectedAccessories={bookingData.selectedAccessories || {}}
            accessories={accessories || []}
            pricing={bookingData.pricing}
            showInseamStep5Hint
          />
        </div>

        {/* Contenido principal */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Grid de bicicletas */}
          <div className="flex-1 relative min-h-[400px]">
            {/* Determinar si debemos mostrar el spinner */}
            {(() => {
              // Mostrar spinner solo si hay rango de fechas y:
              // 1. Está cargando la disponibilidad, O
              // 2. No hay datos aún y no hay error (esperando primera carga)
              // Cuando ya tenemos availabilityData (aunque el filtro devuelva 0 modelos, ej. categoría "Otros"),
              // no mostramos spinner: mostramos el mensaje "No hay bicicletas disponibles..."
              const shouldShowSpinner =
                hasDateRange &&
                (availabilityLoading ||
                  (!availabilityError && availabilityData === null));

              if (shouldShowSpinner) {
                // Si ya hay datos previos y está recargando, mostrar overlay
                if (
                  availabilityData &&
                  Array.isArray(availabilityData) &&
                  availabilityData.length > 0 &&
                  availabilityLoading
                ) {
                  return (
                    <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-lg">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-EBC122 mb-4"></div>
                      <p className="text-2c3e50 text-lg font-medium">
                        {t("bike.loading")}
                      </p>
                    </div>
                  );
                } else {
                  // Spinner inicial cuando no hay datos o están procesando
                  return (
                    <div className="flex flex-col items-center justify-center py-16 absolute inset-0 z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-EBC122 mb-4"></div>
                      <p className="text-2c3e50 text-lg font-medium">
                        {t("bike.loading")}
                      </p>
                    </div>
                  );
                }
              }
              return null;
            })()}

            {!hasDateRange && (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center min-h-[280px] border border-primary/25 rounded-lg bg-white/60">
                <Calendar size={40} className="text-primary mb-4 flex-shrink-0" />
                <p className="text-2c3e50 text-base font-medium max-w-md">
                  {t("bike.dates.required.prompt")}
                </p>
              </div>
            )}

            {hasDateRange && availabilityError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <div className="flex gap-2 items-center text-red-500">
                  <AlertCircle size={20} />
                  <span>{availabilityError}</span>
                </div>
              </div>
            )}
            {!availabilityLoading &&
              !availabilityError &&
              filteredModels?.length === 0 &&
              availabilityData !== null && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                  <div className="flex gap-2 items-center text-yellow-500">
                    <AlertCircle size={20} />
                    <span>{t("bike.no.models")}</span>
                  </div>
                </div>
              )}
            {!availabilityLoading &&
              filteredModels &&
              filteredModels.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-2 lg:px-0">
                  {filteredModels?.map((model) => {
                    const isFlipped = flippedCards.has(model.id);
                    // Obtener las tallas disponibles para este modelo
                    const modelSizes = model.availability?.availableSizes || [];
                    // Usar todas las tallas posibles para calcular el total, pero solo contar las que existen
                    const allSizesToCheck =
                      modelSizes.length > 0 ? modelSizes : ALL_POSSIBLE_SIZES;
                    const totalSelected = allSizesToCheck.reduce(
                      (total, size) => {
                        return total + getSelectedCount(model.id, size);
                      },
                      0
                    );

                    return (
                      <div
                        key={model.id}
                        className={`flip-card-container group ${
                          isFlipped ? "flipped" : ""
                        }`}
                        onMouseEnter={() => {
                          if (shouldUseHoverCardFlip()) {
                            setFlippedCards((prev) =>
                              new Set(prev).add(model.id)
                            );
                          }
                        }}
                        onMouseLeave={() => {
                          if (shouldUseHoverCardFlip()) {
                            setFlippedCards((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(model.id);
                              return newSet;
                            });
                          }
                        }}
                      >
                        <div
                          className={`flip-card ${isFlipped ? "flipped" : ""}`}
                        >
                          {/* Frente de la card - Información de la bicicleta */}
                          <div className="flip-card-front">
                            {/* Indicador de selección en la esquina */}
                            {totalSelected > 0 && (
                              <div className="selection-indicator">
                                {totalSelected}{" "}
                                {totalSelected > 1
                                  ? t("bike.selected.plural")
                                  : t("bike.selected.singular")}
                              </div>
                            )}

                            <div className="p-4">
                              <div className="w-full h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center">
                                <BikeCatalogModelImage
                                  t={t}
                                  src={model.imageUrls?.[0]}
                                  alt={model.name}
                                />
                              </div>
                            </div>
                            <div className="p-4 sm:p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
                              <h3 className="text-lg font-semibold text-2c3e50 mb-2 shrink-0">
                                {model.name}
                              </h3>
                              <div className="min-h-0 flex-1 overflow-y-auto mb-4">
                                <p className="text-6c757d text-sm mb-3">
                                  {model.description}
                                </p>
                                <div className="flex flex-wrap gap-2 items-center">
                                  <span className="font-semibold text-lg text-primary">
                                    {getPriceDescriptionWithContext(model)}
                                  </span>
                                  {model.pricingType === "mixed" && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      🔄 {t("bike.mixed.seasons")}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flip-card-touch-flip-actions mt-1">
                                <button
                                  type="button"
                                  className="w-full bg-EBC122 text-2c3e50 font-semibold py-3 px-4 rounded-lg hover:bg-d4a91e transition-colors duration-200 flex items-center justify-center gap-2 shadow-lg"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCardFlip(model.id);
                                  }}
                                >
                                  <RotateCcw size={18} />
                                  {t("bike.select.sizes")}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Reverso de la card - Selección de tallas */}
                          <div className="flip-card-back">
                            <div className="p-4 sm:p-6 flex-1 flex flex-col">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-2c3e50">
                                  {model.name}
                                </h3>
                                {/* <button
                              className="block lg:hidden w-10 h-10 border border-EBC122 text-EBC122 bg-transparent rounded-lg flex items-center justify-center hover:bg-EBC122 hover:text-2c3e50 transition-colors duration-200 shadow-lg"
                              onClick={() => toggleCardFlip(model.id)}
                              aria-label={t('bike.back.to.info')}
                            >
                              <RotateCcw size={18} />
                            </button> */}
                              </div>

                              <div className="mb-4 flex-shrink-0">
                                {/* <h4 className="font-medium mb-3 text-sm text-white">{t('bike.select.sizes')}</h4> */}

                                {/* Indicador de disponibilidad general */}
                                <div className="availability-indicator mb-3">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-light">
                                      {t("bike.total.availability")}
                                    </span>
                                    <span className="text-primary font-semibold">
                                      {(
                                        model.availability?.availableSizes || []
                                      ).reduce((total, size) => {
                                        const sizeData =
                                          model.availability.bySize[size];
                                        return total + (sizeData?.count || 0);
                                      }, 0)}{" "}
                                      {t("bike.bikes")}
                                    </span>
                                  </div>
                                </div>

                                {/* Grid con máximo 2 columnas (2 tallas por fila) y scroll interno */}
                                {(() => {
                                  const modelSizes =
                                    model.availability?.availableSizes || [];
                                  const rawSizes =
                                    modelSizes.length > 0
                                      ? modelSizes
                                      : ALL_POSSIBLE_SIZES.filter(
                                          (size) =>
                                            model.availability?.bySize?.[size]
                                        );
                                  // Ordenar las tallas de menor a mayor
                                  const sizesToShow = sortSizes(rawSizes);
                                  return (
                                    <div className="sizes-container">
                                      <div className="grid grid-cols-2 gap-2 max-w-full">
                                        {sizesToShow.map((size) => {
                                          const sizeData =
                                            model.availability.bySize[size];
                                          const isAvailable =
                                            sizeData?.count > 0;
                                          const selectedCount =
                                            getSelectedCount(model.id, size);
                                          const availableCount =
                                            sizeData?.count || 0;

                                          return (
                                            <div
                                              key={size}
                                              className={`size-card ${
                                                isAvailable ? "" : "unavailable"
                                              } ${
                                                selectedCount > 0
                                                  ? "selected"
                                                  : ""
                                              }`}
                                            >
                                              <div className="size-card-main">
                                                <div className="size-card-header">
                                                  <span className="text-sm font-semibold text-2c3e50">
                                                    {size}
                                                  </span>
                                                  <span className="text-xs text-6c757d">
                                                    ({availableCount})
                                                  </span>
                                                </div>
                                                <div className="size-card-controls">
                                                  <button
                                                    className="size-button"
                                                    disabled={
                                                      !isAvailable ||
                                                      selectedCount === 0 ||
                                                      !sizeData?.vehicles?.[
                                                        selectedCount - 1
                                                      ]
                                                    }
                                                    onClick={() => {
                                                      const vehicle =
                                                        sizeData?.vehicles?.[
                                                          selectedCount - 1
                                                        ];
                                                      if (vehicle?.id) {
                                                        handleVehicleSelection(
                                                          model.id,
                                                          size,
                                                          vehicle.id,
                                                          "remove"
                                                        );
                                                      }
                                                    }}
                                                    aria-label={`Reducir cantidad de talla ${size}`}
                                                  >
                                                    <Minus
                                                      size={12}
                                                    />
                                                  </button>
                                                  <span className="text-2c3e50 font-semibold text-sm min-w-[20px] text-center">
                                                    {selectedCount}
                                                  </span>
                                                  <button
                                                    className="size-button"
                                                    disabled={
                                                      !isAvailable ||
                                                      selectedCount >=
                                                        availableCount ||
                                                      !sizeData?.vehicles?.[
                                                        selectedCount
                                                      ]
                                                    }
                                                    onClick={() => {
                                                      const vehicle =
                                                        sizeData?.vehicles?.[
                                                          selectedCount
                                                        ];
                                                      if (vehicle?.id) {
                                                        handleVehicleSelection(
                                                          model.id,
                                                          size,
                                                          vehicle.id,
                                                          "add"
                                                        );
                                                      }
                                                    }}
                                                    aria-label={`Aumentar cantidad de talla ${size}`}
                                                  >
                                                    <Plus  size={12} />
                                                  </button>
                                                </div>
                                              </div>
                                              {/* {selectedCount > 0 && (
                                          <div className="size-card-selection">
                                            <span className="text-xs text-primary font-medium">
                                              {selectedCount} seleccionada{selectedCount > 1 ? 's' : ''}
                                            </span>
                                          </div>
                                        )} */}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Precio en el reverso */}
                              <div className="mt-auto flex-shrink-0 relative z-10">
                                {/* <div className="flex gap-2 items-center">
                              <span className="font-semibold text-lg text-primary">
                                {getPriceDescription(model, calculateDurationInDays())}
                              </span>
                            </div> */}

                                <div className="flip-card-touch-flip-actions mt-4 w-full relative">
                                  <button
                                    type="button"
                                    className="w-full bg-6c757d text-white font-semibold py-3 px-4 rounded-lg hover:bg-5a6268 transition-colors duration-200 flex items-center justify-center gap-2 shadow-lg"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleCardFlip(model.id);
                                    }}
                                  >
                                    <RotateCcw size={18} />
                                    {t("common.back")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>

          {/* Resumen de precios - Solo visible en desktop */}
          <div className="hidden lg:block lg:w-80 xl:w-96">
            <div className="lg:sticky lg:top-4 space-y-4">
              <PriceBreakdown
                selectedVehicles={selectedVehicles}
                selectedDates={selectedDates}
                deliveryType={deliveryType}
                selectedAccessories={bookingData.selectedAccessories || {}}
                accessories={accessories || []}
                pricing={bookingData.pricing}
                showInseamStep5Hint
              />
              <button
                type="button"
                className="block px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                style={{ width: '100%' }}
                disabled={selectedVehicles.length === 0}
                onClick={() => {
                  if (selectedVehicles.length > 0) {
                    onNext(selectedVehicles);
                  }
                }}
              >
                {t('common.continue')}
              </button>
            </div>
          </div>
        </div>

        {/* Botones de navegación - abajo en móvil; en desktop el continuar está bajo el desglose de precio */}
        <div className="lg:hidden mt-8">
        <div className="flex sm:flex-row max-lg:justify-between lg:justify-end gap-4 sm:gap-0">
          <div className="block lg:hidden">
            <button
              type="button"
              className="px-8 py-4 bg-primary text-2c3e50 border border-EBC122 bg-transparent font-semibold rounded-lg flex items-center gap-2"
              onClick={onBack}
            >
              {t('common.back')}
            </button>
          </div>
          <button
            type="button"
            className="px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            disabled={selectedVehicles.length === 0}
            onClick={() => {
              if (selectedVehicles.length > 0) {
                onNext(selectedVehicles);
              }
            }}
          >
            {t('common.continue')}
          </button>
        </div>
        </div>
        {/* <div className="flex justify-between mt-8">
        <button
          type="button"
          className="px-8 py-4 bg-primary text-2c3e50 border border-EBC122 bg-transparent font-semibold rounded-lg flex items-center gap-2"
          onClick={onBack}
        >
          {t('common.back')}
        </button>
        <button
          className="px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          onClick={() => {
            if (selectedVehicles.length > 0) {
              onNext(selectedVehicles);
            }
          }}
          disabled={selectedVehicles.length === 0}
        >
          {t('common.continue')}
        </button>
      </div> */}
      </div>
    </div>
  );
};

export default BikeCatalog;
